/**
 * 📦 ORDER STATUS - Gemini Design
 * Beautiful celebration when order is ready!
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navigation, QrCode, CheckCircle, Truck } from 'lucide-react';
import { API_BASE_URL, useOrderStore } from '../store';
import styles from './OrderStatus.module.css';

type OrderStatus = 'pending' | 'preparing' | 'ready' | 'collected';

// Map item IDs to their images
const itemImages: Record<string, string> = {
  'FM001': '/softserve-tran.png', // Classic Mango
  'FM002': '/softserve-swirl.png', // Tropical Twist
  'FM003': '/softserve-transchocdip.png', // Choc Dip Cone
  'FM004': '/softserve-tranvanillalicious.png', // Vanillalicious
};

const getItemImage = (menuItemId: string) => {
  return itemImages[menuItemId] || '/softserve-tran.png';
};

// Topping names for display
const TOPPINGS: Record<string, string> = {
  'choc_sprinkles': 'Choc Sprinkles',
  'rainbow_sprinkles': 'Rainbow Sprinkles',
  'crushed_nuts': 'Crushed Nuts',
  'hot_fudge': 'Hot Fudge',
  'caramel': 'Caramel',
};

const statusSteps = [
  { key: 'pending', label: 'Confirmed' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'ready', label: 'READY!' },
];

export function OrderStatusPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const order = useOrderStore((state) => state.getOrder(id || ''));
  const updateOrderStatus = useOrderStore((state) => state.updateOrderStatus);
  const removeOrder = useOrderStore((state) => state.removeOrder);
  const initialStatus = order?.status || 'pending';
  const [status, setStatus] = useState<OrderStatus>(initialStatus);
  const [pickupWindow, setPickupWindow] = useState(order?.pickupWindow || '3');

  // Track if component is mounted
  const mountedRef = useRef(true);
  const simulationStartedRef = useRef<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Status simulation - runs once per order
  useEffect(() => {
    // If already completed, just set status and exit
    if (initialStatus === 'ready' || initialStatus === 'collected') {
      setStatus(initialStatus);
      return;
    }

    // Prevent double-start for same order (React Strict Mode)
    if (simulationStartedRef.current === id) {
      return;
    }
    simulationStartedRef.current = id || null;

    // Start fresh
    setStatus('pending');

    // Timer 1: pending -> preparing after 3s
    setTimeout(() => {
      if (mountedRef.current) {
        setStatus('preparing');
        if (id) updateOrderStatus(id, 'preparing');
      }
    }, 3000);

    // Timer 2: preparing -> ready after 8s
    setTimeout(() => {
      if (mountedRef.current) {
        setStatus('ready');
        if (id) updateOrderStatus(id, 'ready');
      }
    }, 8000);

    // No cleanup - let timers run to completion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only run when order ID changes

  const getCurrentStepIndex = () => statusSteps.findIndex(s => s.key === status);
  const orderNumber = order?.orderNumber || id?.slice(-3) || '847';
  const isReady = status === 'ready';
  const orderItems = order?.items || [];
  const isDelivery = order?.isDelivery || false;
  const seatInfo = order?.seatInfo;

  const handleMarkCollected = () => {
    if (id) {
      updateOrderStatus(id, 'collected'); // Mark as collected, keep in history
      navigate('/menu');
    }
  };

  return (
    <div className={`${styles.container} ${isReady ? styles.containerReady : ''}`}>
      {/* Celebration Header */}
      {isReady && (
        <div className={styles.celebrationHeader}>
          <span>🎉</span>
          <span>{isDelivery ? 'ON THE WAY!' : 'YOUR ORDER IS READY!'}</span>
          <span>🎉</span>
        </div>
      )}

      {/* Progress Bar */}
      <div className={styles.progressSection}>
        <div className={styles.progressTrack}>
          {statusSteps.map((step, index) => (
            <div key={step.key} className={styles.progressStep}>
              <div 
                className={`${styles.progressDot} ${
                  index <= getCurrentStepIndex() ? styles.progressDotActive : ''
                }`}
              />
              <span className={`${styles.progressLabel} ${
                index <= getCurrentStepIndex() ? styles.progressLabelActive : ''
              }`}>
                {step.label}
              </span>
            </div>
          ))}
          {/* Progress line */}
          <div className={styles.progressLine}>
            <div 
              className={styles.progressFill}
              style={{ width: `${(getCurrentStepIndex() / (statusSteps.length - 1)) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {isReady ? (
          <>
            {/* Pickup/Delivery Location with Order Items */}
            <div className={styles.pickupSection}>
              <span className={styles.pickupIcon}>{isDelivery ? '🚀' : '📍'}</span>
              <span className={styles.pickupLabel}>{isDelivery ? 'DELIVERING TO' : 'PICK UP'}</span>

              {/* Order Items */}
              {orderItems.length > 0 && (
                <div className={styles.orderItems}>
                  {orderItems.map((item, index) => (
                    <div key={item.id || index} className={styles.orderItem}>
                      <div className={styles.orderItemThumb}>
                        <img
                          src={getItemImage(item.menuItemId)}
                          alt={item.name}
                          className={styles.orderItemImg}
                        />
                      </div>
                      <div className={styles.orderItemInfo}>
                        <div className={styles.orderItemHeader}>
                          <span className={styles.orderItemQty}>{item.quantity}x</span>
                          <span className={styles.orderItemName}>{item.name}</span>
                          {item.size && <span className={styles.orderItemSize}>({item.size})</span>}
                        </div>
                        {item.toppings && item.toppings.length > 0 && (
                          <div className={styles.orderItemToppings}>
                            {item.toppings.map((t, i) => (
                              <span key={i} className={styles.toppingTag}>
                                + {TOPPINGS[t] || t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isDelivery ? (
                <>
                  <span className={styles.pickupAt}>YOUR SEAT</span>
                  <div className={styles.windowNumber}>
                    {seatInfo ? `${seatInfo.section}` : 'YOUR SEAT'}
                  </div>
                  <span className={styles.pickupDetails}>
                    {seatInfo ? `Row ${seatInfo.row}, Seat ${seatInfo.number}` : 'Stay seated, we\'re coming!'}
                  </span>
                </>
              ) : (
                <>
                  <span className={styles.pickupAt}>AT</span>
                  <div className={styles.windowNumber}>
                    WINDOW {pickupWindow}
                  </div>
                  <span className={styles.pickupDetails}>Level 1, near Gate B</span>
                </>
              )}
            </div>

            {/* Get Directions Button - only for pickup orders */}
            {!isDelivery && (
              <button
                className={styles.directionsButton}
                onClick={() => navigate('/directions?type=food')}
              >
                <Navigation size={20} />
                GET DIRECTIONS
              </button>
            )}

            {/* Delivery info */}
            {isDelivery && (
              <div className={styles.deliveryNote}>
                <Truck size={20} />
                <span>Our team is bringing your order to you!</span>
              </div>
            )}

            {/* QR Code */}
            <div className={styles.qrSection}>
              <span className={styles.qrLabel}>Show this to staff</span>
              <div className={styles.qrCode}>
                <QrCode size={80} strokeWidth={1.5} />
              </div>
              <span className={styles.orderNumber}>Order #{orderNumber}</span>
            </div>

            {/* Mark as Collected Button */}
            <button
              className={styles.collectedButton}
              onClick={handleMarkCollected}
            >
              <CheckCircle size={20} />
              I Got My Order!
            </button>
          </>
        ) : (
          <>
            {/* Waiting State */}
            <div className={styles.waitingSection}>
              <div className={styles.waitingIcon}>
                {status === 'preparing' && orderItems.length > 0 ? (
                  <img
                    src={getItemImage(orderItems[0].menuItemId)}
                    alt={orderItems[0].name}
                    className={styles.waitingImg}
                  />
                ) : (
                  <span>{status === 'preparing' ? '🍦' : '✓'}</span>
                )}
              </div>
              <h2 className={styles.waitingTitle}>
                {status === 'preparing'
                  ? "We're making your treats!"
                  : 'Order Confirmed!'
                }
              </h2>
              <p className={styles.waitingSubtitle}>
                Estimated time: 3-5 minutes
              </p>
            </div>

            {/* Notification */}
            <div className={styles.notifyCard}>
              <span>🔔</span>
              <span>We'll notify you when ready!</span>
            </div>
          </>
        )}
      </div>

      {/* Back Link */}
      <button 
        className={styles.backLink}
        onClick={() => navigate('/menu')}
      >
        Back to Menu
      </button>
    </div>
  );
}
